import { EventManager } from './EventManager';
import { VisualManager } from './VisualManager';
import { ScrollManager } from './ScrollManager';
import { CompatibilityDetector } from '../compatibility';
import {
	DndSystemConfig,
	DraggableOptions,
	DropZoneConfig,
	DraggableContext,
	DropZoneContext,
	DraggableStrategy,
	DropZoneStrategy,
	DragData,
	DropPosition,
	DropResult,
	DraggableConfig
} from '../types';
import {DEFAULT_DND_CONFIG, DRAG_DEFAULT_CONFIG} from './../config';
import {IsDeviceMobile} from "../../utils/DeviceDetector";

const DndDebug = {
	debug: 0
};
(window as any).dndDebug = DndDebug;

export class DndSystem {
	private readonly MOVE_DETECTION_THRESHOLD_PX = 10; // Порог для детектирования любого движения
	private readonly SWIPE_DETECTION_THRESHOLD_PX = 25; // Минимальное расстояние для распознавания свайпа
	private readonly SCROLL_DETECTION_THRESHOLD = 10; // Порог для определения скролла
	private readonly SHORT_CLICK_TIMEOUT_MS = 150; // Максимальное время для короткого нажатия
	private readonly POST_SCROLL_BLOCK_DURATION_MS = 200; // Время блокировки драга после скролла

	private static instance: DndSystem;

	private config: DndSystemConfig;
	private eventManager: EventManager;
	private visualManager: VisualManager;
	private scrollManager: ScrollManager;
	private compatibilityDetector: CompatibilityDetector;

	private originalEvent: MouseEvent | TouchEvent | PointerEvent | null = null;

	private draggables: Map<HTMLElement, DraggableOptions> = new Map();
	private dropZones: Map<HTMLElement, DropZoneConfig> = new Map();

	private currentDrag: {
		data: DragData;
		element: HTMLElement;
		startX: number;
		startY: number;
		startTime: number;
	} | null = null;

	private currentDropZone: {
		config: DropZoneConfig;
		position: DropPosition;
	} | null = null;

	private activationTimer: number | null = null;
	private isActivating = false;
	private isShortClick = false;
	private isDragStarted = false;
	private isDragging = false;
	private hasMoved = false;
	private ShortClickTimout: number = this.SHORT_CLICK_TIMEOUT_MS;
	private clickTimer: any;

	// Переменные для отслеживания скролла
	private lastScrollEndTime: number = 0; // Время последнего скролла
	private isScrolling = false; // Флаг, указывающий, что сейчас происходит скролл

	private pageVisibilityHandlers?: {
		handleVisibilityChange: () => void;
		handleWindowBlur: () => void;
		handlePageHide: () => void;
	};

	private constructor(config?: Partial<DndSystemConfig>) {
		this.config = { ...DEFAULT_DND_CONFIG, ...config };
		this.compatibilityDetector = CompatibilityDetector.getInstance();

		this.eventManager = EventManager.getInstance(this.config.compatibility);
		this.visualManager = new VisualManager(this.config.visual);
		this.scrollManager = new ScrollManager(this.config.scroll);

		this.setupGlobalListeners();
	}

	/**
	 * Destroy DndSystem instance and clean up all listeners
	 */
	destroy(): void {
		this.cleanupPageVisibilityListeners();
		this.cancelCurrentDrag();
		this.eventManager.destroy();
		DndSystem.instance = null!;
	}

	static getInstance(config?: Partial<DndSystemConfig>): DndSystem {
		if (!DndSystem.instance) {
			DndSystem.instance = new DndSystem(config);
		}
		return DndSystem.instance;
	}

	/**
	 * Регистрация draggable элемента
	 */
	registerDraggable(options: DraggableOptions): () => void {
		const { context, strategy, config } = options;
		const { element } = context;

		this.draggables.set(element, options);

		// Регистрируем обработчики событий
		this.eventManager.registerDraggable(element, {
			onMouseDown: (e) => this.handleDragStart(e, element, false),
			onTouchStart: (e) => this.handleDragStart(e, element, true),
			onPointerDown: (e) => this.handleDragStart(e, element, e.pointerType === 'touch'),
			onMouseUp: (e) => this.cancelClickTimer("mouseup"),
			onTouchCancel: (e) => this.cancelClickTimer("touch_cancel"),
			onTouchEnd: (e) => this.cancelClickTimer("touch_end"),
			onPointerUp: (e) => this.cancelClickTimer("pointer_up"),
			onClick: (e) => {
				e.stopPropagation();
				e.preventDefault();
			}
		});

		// Функция для отмены регистрации
		return () => {
			this.draggables.delete(element);
			this.eventManager.unregisterDraggable(element);

			if (this.currentDrag?.element === element) {
				this.cancelCurrentDrag();
			}
		};
	}

	private cancelClickTimer(cause:string) {
		if (this.clickTimer) {
			if (DndDebug.debug) console.log(`${Date.now()} cancel click timer`, cause);
			clearTimeout(this.clickTimer);
			this.clickTimer = null;
		}
	}

	/**
	 * Регистрация drop zone
	 */
	registerDropZone(config: DropZoneConfig): () => void {
		const { context } = config;
		const { element } = context;

		this.dropZones.set(element, config);

		// Функция для отмены регистрации
		return () => {
			this.dropZones.delete(element);

			if (this.currentDropZone?.config.context.element === element) {
				this.clearDropZone();
			}
		};
	}

	/**
	 * Принудительная отмена текущего перетаскивания
	 */
	cancelCurrentDrag(): void {
		this.cancelClickTimer("cancel drag");
		if (this.activationTimer) {
			clearTimeout(this.activationTimer);
			this.activationTimer = null;
		}

		this.isActivating = false;
		this.isDragStarted = false; // ← ВАЖНО: сбросить этот флаг!
		this.isDragging = false;
		this.isShortClick = false;
		this.hasMoved = false;
		
		if (this.currentDrag) {
			const options = this.draggables.get(this.currentDrag.element);
			if (options) {
				// Вызываем onDragCancel для завершения перетаскивания
				options.strategy.onDragCancel?.(options.context);
			}
			this.visualManager.clearAll();
		}

		this.clearDropZone();
		this.scrollManager.stop();
		// Notify EventManager to restore normal touch behavior
		this.eventManager.setDraggingState(false);
		this.currentDrag = null;
		this.originalEvent = null; // Также сбрасываем оригинальное событие

		// Устанавливаем время последнего скролла при отмене драга
		this.lastScrollEndTime = performance.now();
	}

	/**
	 * Проверка, активно ли перетаскивание
	 */
	isDragActive(): boolean {
		return this.isDragging || this.isActivating || this.isDragStarted;
	}

	/**
	 * Получение текущих данных перетаскивания
	 */
	getCurrentDrag(): DragData | null {
		return this.currentDrag?.data || null;
	}

	/**
	 * Получение информации о браузерной совместимости
	 */
	getCompatibilityInfo() {
		return this.compatibilityDetector.getCapabilities();
	}

	/**
	 * Получение стратегии по умолчанию
	 */
	static defaultStrategy(): DraggableStrategy {
		return {
			onActivate: (context) => {
				console.log('Default activate:', context.id);
			},
			onDragStart: (context, dragData) => {
				console.log('Default drag start:', context.id);
			},
			onDragEnd: (context, result) => {
				console.log('Default drag end:', result.success);
			}
		};
	}

	// ========== PRIVATE METHODS ==========

	private setupGlobalListeners(): void {
		// Глобальные обработчики для отслеживания движения
		this.eventManager.registerGlobal({
			onMouseMove: (e) => this.handleDragMove(e.clientX, e.clientY, false),
			onTouchMove: (e) => {
				if (e.touches.length === 1) {
					const touch = e.touches[0];
					this.handleDragMove(touch.clientX, touch.clientY, true);

					// Предотвращаем скролл страницы только при активном DnD
					if (this.isDragging) {
						e.preventDefault();
					}
				}
			},
			onPointerMove: (e) => {
				this.handleDragMove(e.clientX, e.clientY, e.pointerType === 'touch');
			},
			onMouseUp: (e) => this.handleDragEnd(e),
			onTouchEnd: (e) => this.handleDragEnd(e),
			onPointerUp: (e) => this.handleDragEnd(e),
		});

		// Add visibility/blur listeners for forced drag cancellation
		this.setupPageVisibilityListeners();
	}

	private setupPageVisibilityListeners(): void {
		// Handle page visibility change (app minimized/background)
		const handleVisibilityChange = () => {
			if (document.hidden && this.isDragActive()) {
				console.log('Page became hidden during drag, cancelling...');
				this.cancelCurrentDrag();
			}
		};

		// Handle window blur (app lost focus)
		const handleWindowBlur = () => {
			if (this.isDragActive()) {
				console.log('Window lost focus during drag, cancelling...');
				this.cancelCurrentDrag();
			}
		};

		// Handle page hide (especially for mobile)
		const handlePageHide = () => {
			if (this.isDragActive()) {
				console.log('Page hiding, cancelling drag...');
				this.cancelCurrentDrag();
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('blur', handleWindowBlur);
		window.addEventListener('pagehide', handlePageHide);

		// Store for cleanup
		this.pageVisibilityHandlers = {
			handleVisibilityChange,
			handleWindowBlur,
			handlePageHide
		};
	}

	/**
	 * Cleanup page visibility listeners
	 */
	private cleanupPageVisibilityListeners(): void {
		if (this.pageVisibilityHandlers) {
			document.removeEventListener('visibilitychange', this.pageVisibilityHandlers.handleVisibilityChange);
			window.removeEventListener('blur', this.pageVisibilityHandlers.handleWindowBlur);
			window.removeEventListener('pagehide', this.pageVisibilityHandlers.handlePageHide);
			this.pageVisibilityHandlers = undefined;
		}
	}

	private handleDragStart(event: MouseEvent | TouchEvent | PointerEvent,
		element: HTMLElement,
		isTouch: boolean): void {
		// Проверяем, не было ли недавно скролла
		const currentTime = performance.now();
		if (currentTime - this.lastScrollEndTime < this.POST_SCROLL_BLOCK_DURATION_MS) {
			// Слишком рано после скролла, игнорируем попытку драга
			return;
		}

		if (this.isDragActive()) return;

		const options :DraggableOptions|undefined = this.draggables.get(element);
		if (!options || options.config?.disabled) return;

		// Сохраняем оригинальное событие для обработки клика
		this.originalEvent = event;
		// Предотвращаем стандартное поведение для DnD
		this.originalEvent.preventDefault();
		this.originalEvent.stopPropagation()

		// Получаем координаты
		let clientX: number, clientY: number;

		if ('touches' in event) {
			// Touch event
			const touch = event.touches[0];
			if (!touch) return;
			clientX = touch.clientX;
			clientY = touch.clientY;
		} else {
			// Mouse or Pointer event
			clientX = event.clientX;
			clientY = event.clientY;
		}

		// Сохраняем начальные данные
		this.currentDrag = {
			element,
			startX: clientX,
			startY: clientY,
			startTime: performance.now(),
			data: {
				id: options.context.id,
				type: options.context.type,
				data: options.context.data,
				element,
				startRect: element.getBoundingClientRect(),
				offset: { x: 0, y: 0 }
			}
		};

		// Вычисляем offset относительно элемента
		const rect = element.getBoundingClientRect();
		this.currentDrag.data.offset = {
			x: clientX - rect.left,
			y: clientY - rect.top
		};

		// Сбрасываем флаги
		this.isShortClick = false;
		this.hasMoved = false;

		if (DndDebug.debug) console.log(`${Date.now()} start click timer`, event);
		// Запускаем таймер активации сразу, без ожидания таймера клика
		// Это обеспечивает более надежную активацию драга
		this.cancelClickTimer("start new timer");
		this.clickTimer = setTimeout(()=>{
			this.startActivationTimer(options);	
		}, this.ShortClickTimout)
		
	}

	private startActivationTimer(options: DraggableOptions): void {
		// Проверяем, было ли движение до запуска таймера активации
		if (this.hasMoved) {
			// Если уже было движение, не запускаем таймер активации
			return;
		}

		this.isActivating = true;
		this.hasMoved = false;
		this.isDragStarted = false;

		// Визуальная обратная связь
		options.strategy.onActivate?.(options.context);
		const element = this.currentDrag?.element;
		if  (!element) {
			console.error("No drag element found");
			return;
		}
		this.visualManager.showActivationFeedback(element, options.strategy.cssClasses?.activating);

		const activationDelay = options.config?.activationDelay || (IsDeviceMobile?2000:200);

		this.activationTimer = window.setTimeout(() => {
			if (DndDebug.debug) console.log(`${Date.now()} start activation timer expired`);
			if (this.isActivating &&
				this.currentDrag &&
				!this.hasMoved &&
				!this.isShortClick) {

				// Устанавливаем флаг, что перетаскивание началось
				// Это нужно для правильного определения isDragActive()
				this.isDragStarted = true;

				this.activateDrag();
			}
			else {
				if (DndDebug.debug) console.log(`${Date.now()}  no drag:${this.currentDrag}, moved:${this.hasMoved}, not activated:${this.isActivating}`);
			}
		}, activationDelay);
		if (DndDebug.debug) console.log(`${Date.now()} start activation timer`, activationDelay);
	}

	private activateDrag(): void {
		if (!this.currentDrag) {
			console.log("no drag");
			return;
		}

		const options = this.draggables.get(this.currentDrag.element);
		if (!options) {
			console.log("no options");
			return;
		}

		this.isActivating = false;
		this.isDragging = true;
		this.isDragStarted = true;
		this.activationTimer = null;

		// Notify EventManager to block pull-to-refresh
		this.eventManager.setDraggingState(true);

		// Уведомляем клиента
		options.strategy.onDragStart?.(options.context, this.currentDrag.data);

		// Создаем превью
		this.visualManager.createDragPreview(
			this.currentDrag.element,
			this.currentDrag.data.offset!
		);

		// Запускаем автоскролл
		this.scrollManager.start();
	}


	private handleDragMove(clientX: number, clientY: number, isTouch: boolean): void {
		if (!this.currentDrag) return;

		const options = this.draggables.get(this.currentDrag.element);
		if (!options) return;

		// Отслеживаем движение с самого начала
		const distanceX = Math.abs(clientX - this.currentDrag.startX);
		const distanceY = Math.abs(clientY - this.currentDrag.startY);
		const movementThreshold = options.config?.movementThreshold || this.MOVE_DETECTION_THRESHOLD_PX; // Используем порог для детектирования движения

		// Обновляем флаг hasMoved при любом движении, превышающем порог
		if (!this.hasMoved
			&& (distanceX > movementThreshold
			|| distanceY > movementThreshold)) {
			this.hasMoved = true;

			// Если таймер активации еще не запущен, отменяем его
			// Если уже запущен, проверяем, нужно ли отменить
			if (this.activationTimer && this.isActivating) {
				// Отменяем таймер активации, если уже было движение
				clearTimeout(this.activationTimer as any);
				this.activationTimer = null;
				this.isActivating = false;
				// Отменяем текущий драг
				this.cancelCurrentDrag();
				return;
			}
		}
		//console.log(`${Date.now()} move`, distanceX, distanceY, this.hasMoved);

		// Проверяем движение во время активации
		if (this.isActivating && !this.isShortClick) {
			const movedX = Math.abs(clientX - this.currentDrag.startX);
			const movedY = Math.abs(clientY - this.currentDrag.startY);

			// Используем централизованный метод для определения типа движения
			const movementType = this.detectMovementType(movedX, movedY);

			switch (movementType) {
			case 'vertical_swipe':
				// Это вертикальный свайп (скролл), отменяем драг
				options.strategy.onScrollActivation?.(options.context);
				this.cancelCurrentDrag();
				// Устанавливаем время последнего скролла
				this.lastScrollEndTime = performance.now();
				return;
			case 'horizontal_drag':
				// Это горизонтальное движение, возможно драг
				// Пока не отменяем, даем возможность активации
				break;
			case 'movement_detected':
				// Любое движение больше порога детекции, но не явный свайп
				// Отменяем DnD в пользу скролла или обычного движения
				options.strategy.onScrollActivation?.(options.context);
				this.cancelCurrentDrag();
				// Устанавливаем время последнего скролла
				this.lastScrollEndTime = performance.now();
				return;
			case 'no_movement':
				// Нет значительного движения
				break;
			}
		}

		// Обновляем позицию при активном перетаскивании
		if (this.isDragging) {
			// Обновляем превью с учетом offset
			const previewX = Math.floor(clientX - this.currentDrag.data.offset!.x);
			const previewY = Math.floor(clientY - this.currentDrag.data.offset!.y);
			// console.log(`${Date.now()} update preview`, previewX, previewY);
			this.visualManager.updateDragPreview(previewX, previewY);

			// Обновляем автоскролл
			this.scrollManager.update(clientX, clientY);

			// Ищем дроп-зону под курсором
			this.updateDropZoneUnderCursor(clientX, clientY);
		}
	}

	private updateDropZoneUnderCursor(x: number, y: number): void {
		if (!this.currentDrag) return;

		// Ищем все подходящие дроп-зоны
		for (const [element, config] of this.dropZones) {
			const { context, strategy } = config;

			// Проверяем совместимость типов
			if (!context.acceptTypes.includes(this.currentDrag.data.type)) {
				continue;
			}

			const rect = element.getBoundingClientRect();

			// Проверяем попадание в bounding box
			if (x >= rect.left && x <= rect.right &&
				y >= rect.top && y <= rect.bottom) {

				// Вызываем стратегию для определения позиции
				const position = strategy.calculateDropPosition(
					x, y, context, this.currentDrag.data
				);

				if (position) {
					// Проверяем, является ли это та же дроп-зона, что и текущая
					const isSameDropZone = this.currentDropZone &&
						this.currentDropZone.config.context.element === element;

					// Если это новая дроп-зона или текущая дроп-зона изменила позицию
					if (!this.currentDropZone || !isSameDropZone) {
						// Уведомляем о выходе из предыдущей зоны
						const dropZoneConfig = this.currentDropZone?.config;
						//@ts-ignore
						dropZoneConfig?.strategy.onDragLeave?.(
							dropZoneConfig.context,
							this.currentDrag.data
						);

						// Уведомляем о входе в новую зону
						strategy.onDragEnter?.(context, this.currentDrag.data, position);

						this.currentDropZone = { config, position };

						// Создаем индикатор
						this.visualManager.createDropIndicator(
							position,
							strategy.cssClasses?.active
						);

						if (DndDebug.debug) console.log(`${Date.now()} dropzone changed`);
					}
					// Если это та же дроп-зона, обновляем позицию только если она изменилась значительно
					else if (this.currentDropZone && isSameDropZone) {
						// Проверяем, изменилась ли позиция достаточно, чтобы обновить
						const currentPosition = this.currentDropZone.position;
						const positionChanged = currentPosition.relativeX !== position.relativeX ||
											   currentPosition.relativeY !== position.relativeY ||
											   JSON.stringify(currentPosition.data) !== JSON.stringify(position.data);

						if (positionChanged) {
							this.currentDropZone.position = position;
							this.visualManager.updateDropIndicator(
								position,
								strategy.cssClasses?.active
							);
						}
					}

					return;
				}
			}
		}

		// Курсор вне всех дроп-зон
		if (this.currentDropZone) {
			if (DndDebug.debug) console.log(`${Date.now()} dropzone clear`);
			this.clearDropZone();
		}
	}

	private clearDropZone(): void {
		if (this.currentDropZone && this.currentDrag) {
			this.currentDropZone.config.strategy.onDragLeave?.(
				this.currentDropZone.config.context,
				this.currentDrag.data
			);
		}

		this.currentDropZone = null;
		this.visualManager.clearDropIndicator();
	}

	private handleDragEnd(e: Event): void {
		e?.stopPropagation?.();
		if (!this.currentDrag) {
			// Если была только активация - отменяем
			if (this.isActivating) {
				this.cancelCurrentDrag();
			}
			return;
		}

		const options = this.draggables.get(this.currentDrag.element);
		if (!options) {
			this.cancelCurrentDrag();
			return;
		}

		// Проверяем, был ли это короткий клик
		// Если таймер активации все еще работает, значит пользователь отпустил до окончания таймера - это короткий клик
		const dragDuration = performance.now() - this.currentDrag.startTime;
		const isShortClick = dragDuration < this.ShortClickTimout && !this.hasMoved && !this.isDragging;

		// Если это короткий клик - обрабатываем как клик
		if (isShortClick) {
			// Отменяем таймер активации если он есть
			if (this.activationTimer) {
				clearTimeout(this.activationTimer);
				this.activationTimer = null;
			}

			// Сбрасываем визуальные эффекты
			this.visualManager.clearAll();

			// Восстанавливаем нормальное поведение для кликов
			this.eventManager.setDraggingState(false);

			// Вызываем обработчик короткого клика если есть
			setTimeout(() => {
				options.strategy.onShortClick?.(options.context, this.getOriginalEvent());
			}, 0);


			// Уведомляем о отмене драга
			options.strategy.onDragCancel?.(options.context);

			// Сбрасываем состояние
			this.resetDragState();
			return;
		}

		let dropResult: DropResult = { success: false };

		// Обрабатываем дроп, если есть активная зона
		if (this.currentDropZone && this.isDragging) {
			const { config, position } = this.currentDropZone;
			dropResult = config.strategy.onDrop?.(
				config.context,
				this.currentDrag.data,
				position
			) || { success: true };
		}

		// Уведомляем клиента о завершении перетаскивания в любом случае
		if (this.isDragging || this.isDragStarted) {
			// Вызываем onDragEnd для завершения перетаскивания
			options.strategy.onDragEnd?.(options.context, dropResult);
		} else if (this.isActivating) {
			// Если был только таймер активации - это клик/тап
			options.strategy.onDragCancel?.(options.context);
		} else {
			// В любом другом случае, если было начато перетаскивание, вызываем onDragCancel
			options.strategy.onDragCancel?.(options.context);
		}

		// Очищаем состояние
		this.completeDrag();
	}

	// Новый метод для сброса состояния драга
	private resetDragState(): void {
		this.isActivating = false;
		this.isDragging = false;
		this.isDragStarted = false; // ← ВАЖНО: сбросить этот флаг!
		this.hasMoved = false;
		this.activationTimer = null;
		this.currentDrag = null;
		this.currentDropZone = null;
		this.scrollManager.stop();
		this.eventManager.setDraggingState(false)
	}

	// Метод для получения оригинального события (нужно добавить сохранение события в handleDragStart)
	private getOriginalEvent(): MouseEvent | TouchEvent | PointerEvent {
		// В handleDragStart нужно сохранить оригинальное событие
		return this.originalEvent!;
	}



	private completeDrag(): void {
		this.scrollManager.stop();
		// Clear visual elements
		this.visualManager.clearAll();

		// Restore normal touch behavior (CRITICAL: was missing!)
		this.eventManager.setDraggingState(false);

		this.clearDropZone();

		this.isActivating = false;
		this.isDragging = false;
		this.hasMoved = false;
		this.isShortClick = false;
		this.isDragStarted = false;
		this.activationTimer = null;
		this.currentDrag = null;
		this.currentDropZone = null;
		this.originalEvent = null;
	}

	/**
	 * Централизованный метод для определения типа движения
	 * @param movedX - абсолютное движение по оси X от начальной точки
	 * @param movedY - абсолютное движение по оси Y от начальной точки
	 * @returns тип движения
	 */
	detectMovementType(movedX: number, movedY: number): "no_movement" | "movement_detected" | "vertical_swipe" | "horizontal_drag" {
		// Проверяем, является ли движение в основном вертикальным (скролл)
		if (movedY > movedX && movedY > this.SWIPE_DETECTION_THRESHOLD_PX) {
			return 'vertical_swipe'; // Это вертикальный свайп (скролл)
		} else if (movedX > movedY && movedX > this.SWIPE_DETECTION_THRESHOLD_PX) {
			return 'horizontal_drag'; // Это горизонтальный драг
		} else if (movedX > this.MOVE_DETECTION_THRESHOLD_PX || movedY > this.MOVE_DETECTION_THRESHOLD_PX) {
			return 'movement_detected'; // Любое движение больше порога детекции, но не явный свайп
		} else {
			return 'no_movement'; // Нет значительного движения
		}
	}
}